/**
 * ShopInventoryDrawer.jsx — slide-over panel listing the user's owned shop
 * items grouped by category, with equip/unequip toggles for cosmetics and
 * read-only summaries for boosters / streak tokens / vault keys.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Backpack, Loader2, X, Sparkles, Shield, Zap, Lock, Gift, CheckCircle2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_ICONS = {
  cosmetic: Sparkles,
  streak:   Shield,
  booster:  Zap,
  vault:    Lock,
  mystery:  Gift,
};

const formatExpires = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hrs = Math.floor(ms / 3.6e6);
  if (hrs >= 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h left`;
  const mins = Math.floor((ms % 3.6e6) / 6e4);
  return `${hrs}h ${mins}m left`;
};

const ShopInventoryDrawer = ({ open, onClose, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/shop/inventory`);
      if (!r.ok) return;
      const d = await r.json();
      setItems(d.inventory || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleEquip = async (inv) => {
    setBusyId(inv.inventory_id);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/shop/inventory/${inv.inventory_id}/equip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ equip: !inv.equipped }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(d.equipped ? `Equipped: ${inv.name}` : `Unequipped: ${inv.name}`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Could not toggle');
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  const grouped = items.reduce((acc, it) => {
    const k = it.category || 'misc';
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="shop-inventory-drawer"
    >
      <div
        className="w-full sm:w-[480px] max-w-full h-full bg-slate-900 border-l border-slate-700 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-md border-b border-slate-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Backpack className="w-5 h-5 text-rose-300" />
            <h2 className="text-lg font-bold text-white">Your inventory</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
            aria-label="Close"
            data-testid="inventory-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-400">
              <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <Backpack className="w-8 h-8 text-slate-700 mx-auto mb-2" />
              No items yet. Buy something from the shop and it'll show up here.
            </div>
          ) : (
            Object.entries(grouped).map(([cat, list]) => {
              const Icon = CAT_ICONS[cat] || Sparkles;
              return (
                <section key={cat}>
                  <h3 className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5" /> {cat}
                  </h3>
                  <ul className="space-y-2">
                    {list.map((inv) => (
                      <li
                        key={inv.inventory_id}
                        className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700"
                        data-testid={`inventory-row-${inv.inventory_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{inv.name}</p>
                          {inv.gifted_by_name && (
                            <p className="text-[11px] text-rose-300 mt-0.5 flex items-center gap-1">
                              <span>🎁 Gift from {inv.gifted_by_name}</span>
                            </p>
                          )}
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            {cat === 'booster' && inv.expires_at && (
                              <span className={inv.expires_at && new Date(inv.expires_at).getTime() > Date.now() ? 'text-emerald-300' : 'text-slate-500'}>
                                {formatExpires(inv.expires_at)}
                              </span>
                            )}
                            {cat === 'streak' && (
                              <span>+{inv.days || 1}-day freeze</span>
                            )}
                            {cat === 'vault' && (
                              <span>Unlocks one vault item</span>
                            )}
                            {cat === 'cosmetic' && inv.subtype && (
                              <span>{inv.subtype.replace(/_/g, ' ')}</span>
                            )}
                          </p>
                        </div>
                        {cat === 'cosmetic' ? (
                          <Button
                            size="sm"
                            variant={inv.equipped ? 'default' : 'outline'}
                            onClick={() => toggleEquip(inv)}
                            disabled={busyId === inv.inventory_id}
                            className={inv.equipped
                              ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold'
                              : 'border-slate-600 text-slate-200'}
                            data-testid={`equip-btn-${inv.inventory_id}`}
                          >
                            {busyId === inv.inventory_id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : inv.equipped
                                ? (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Equipped</>)
                                : 'Equip'}
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopInventoryDrawer;
